'use client';

import { apiClient } from '@/lib/api';
import type {
  MysteryRoomAssetFit,
  MysteryRoomAssetTransform,
  MysteryRoomCustomObjectDefinition,
  MysteryRoomGameMode,
  MysteryRoomMapDefinition,
  MysteryRoomTextureLayer,
} from '../world/mapTypes';

export function mrApiErrorMessage(error: unknown, fallback: string): string {
  const candidate = typeof (error as any)?.message === 'string' ? (error as any).message.trim() : '';
  if (!candidate) return fallback;
  const lower = candidate.toLowerCase();
  const looksLikeHtml =
    lower.includes('<!doctype') ||
    lower.includes('<html') ||
    lower.includes('</body>') ||
    lower.includes('__next') ||
    lower.includes('this page could not be found');
  if (looksLikeHtml) return fallback;
  if (candidate.length > 220) return `${candidate.slice(0, 217)}...`;
  return candidate;
}

export type MrProfileResponse = {
  ok: boolean;
  profile: {
    userId: number;
    money: number;
    rating: number;
    matchesPlayed: number;
    crimesCompleted: number;
    crimesExposed: number;
    defenderWins: number;
  };
};

export type MrStatsPlayer = {
  rank: number;
  userId: number;
  name: string;
  avatar?: string | null;
  money: number;
  rating: number;
  matchesPlayed: number;
  crimesCompleted: number;
  crimesExposed: number;
  defenderWins: number;
  updatedAt: string;
};

export type MrStatsResponse = {
  ok: boolean;
  players: MrStatsPlayer[];
};

export type MrFriend = {
  id: number;
  username: string;
  display_name: string;
  avatar_url?: string | null;
  is_online?: boolean;
  last_seen?: string | null;
  game_presence?: MrGamePresence | null;
};

export type MrGamePresence = {
  game_id: string;
  game_title: string;
  state: string;
  state_label?: string | null;
  label: string;
  detail?: string | null;
  map_id?: string | null;
  map_title?: string | null;
  match_id?: number | null;
  lobby_id?: number | null;
  url?: string | null;
  updated_at?: string | null;
};

export type MrMapAsset = {
  id: string;
  assetKey: string;
  name: string;
  filename: string;
  url: string;
  type: string;
  size: number;
  contentHash?: string;
  createdAt: string;
};

export type MrMapPreview = {
  width: number;
  height: number;
  rooms: Array<{
    id: string;
    title: string;
    x: number;
    y: number;
    width: number;
    height: number;
    color: string;
  }>;
  spawnSlots: Array<{
    x: number;
    y: number;
  }>;
  objectsCount: number;
  lightsCount: number;
};

export type MrMapSummary = {
  id: string;
  mapKey: string;
  title: string;
  /** Сужение режимов лобби; null/отсутствует — все режимы (сервер читает из map_json). */
  playableGameModes?: MysteryRoomGameMode[] | null;
  description: string;
  status: 'draft' | 'published' | string;
  schemaVersion: number;
  ownerUserId: number;
  ownerName: string;
  assetBytesTotal: number;
  matchesTotal?: number;
  activeMatches?: number;
  uniquePlayersTotal?: number;
  preview?: MrMapPreview | null;
  publishedAt: string | null;
  updatedAt: string;
  createdAt: string;
};

export type MrMapFull = MrMapSummary & {
  map: MysteryRoomMapDefinition;
  assets: MrMapAsset[];
};

export type MrMapsResponse = {
  ok: boolean;
  maps: MrMapSummary[];
};

export type MrMapResponse = {
  ok: boolean;
  map: MrMapFull;
};

export type MrMapAssetResponse = {
  ok: boolean;
  asset: MrMapAsset;
  reused?: boolean;
};

export type MrObjectSkinPayload = Partial<Pick<
  MysteryRoomCustomObjectDefinition,
  | 'assetUrl'
  | 'assetName'
  | 'assetMimeType'
  | 'assetAlphaCollisionEnabled'
  | 'textures'
  | 'colorEnabled'
  | 'colorOpacity'
  | 'outlineEnabled'
  | 'outlineOpacity'
  | 'outlineWidth'
>> & {
  assetFit?: MysteryRoomAssetFit;
  assetTransform?: MysteryRoomAssetTransform;
  textures?: MysteryRoomTextureLayer[];
};

export type MrObjectSkin = {
  id: string;
  kind: MysteryRoomCustomObjectDefinition['kind'] | string;
  itemId: string;
  title: string;
  isDefault: boolean;
  payload: MrObjectSkinPayload;
  createdByUserId: number;
  updatedByUserId: number;
  createdAt: string;
  updatedAt: string;
};

export type MrObjectSkinsResponse = {
  ok: boolean;
  skins: MrObjectSkin[];
  canManage: boolean;
};

export type MrObjectSkinResponse = {
  ok: boolean;
  skin: MrObjectSkin;
};

export type MrQueuePollResponse = {
  ok: boolean;
  status: 'idle' | 'waiting' | 'matched';
  matchId?: number | null;
  queue?: {
    status: 'idle' | 'waiting' | 'matched' | 'left';
    players: number;
    requiredPlayers: number;
    matchId?: number | null;
  };
};

export type MrLobbyResponse = {
  ok: boolean;
  status?: 'waiting' | 'matched';
  matchId?: number | null;
  left?: boolean;
  lobby: {
    id: number;
    mapId?: string;
    lobbyCode: string | null;
    isPrivate: boolean;
    status: 'open' | 'ready' | 'starting' | 'closed';
    requiredPlayers: number;
    ownerUserId: number;
    settings?: MrLobbySettings;
    slots?: MrLobbySlot[];
    unassignedPlayers?: Array<{
      userId: number;
      displayName: string;
      characterId: string;
      slotIndex?: number | null;
      teamKey?: string;
      slotType?: 'human' | 'bot' | string;
      isReady: boolean;
      joinedAt: string;
      lastSeenAt: string;
      isBot?: boolean;
      botDifficulty?: MrBotDifficulty | string | null;
      botStrategy?: string | null;
    }>;
    players: Array<{
      userId: number;
      displayName: string;
      characterId: string;
      slotIndex?: number | null;
      teamKey?: string;
      slotType?: 'human' | 'bot' | string;
      isReady: boolean;
      joinedAt: string;
      lastSeenAt: string;
      isBot?: boolean;
      botDifficulty?: MrBotDifficulty | string | null;
      botStrategy?: string | null;
    }>;
  } | null;
};

export type MrGameMode = 'classic' | 'training' | 'incident_drill' | 'sabotage';
export type MrBotDifficulty = 'easy' | 'normal' | 'hard';
export type MrIncidentLevel = 'calm' | 'standard' | 'critical';

export type MrLobbySettings = {
  gameMode: MrGameMode | string;
  botDifficulty: MrBotDifficulty | string;
  incidentLevel: MrIncidentLevel | string;
  maxPlayers: number;
};

export type MrLobbySlot = {
  index: number;
  status: 'open' | 'closed' | 'bot' | 'human' | string;
  teamKey: string;
  player: {
    userId: number;
    displayName: string;
    characterId: string;
    isReady: boolean;
    isBot?: boolean;
    botDifficulty?: MrBotDifficulty | string | null;
    botStrategy?: string | null;
  } | null;
};

export type MrLobbyBrowserEntry = {
  lobbyId: number;
  lobbyCode: string | null;
  mapId: string;
  mapTitle: string;
  gameMode: string;
  players: number;
  maxPlayers: number;
  isPrivate: boolean;
  isOfficialMap: boolean;
  region: string;
  pingHint: number;
  ownerUserId: number;
  ownerName: string;
  createdAt: string;
  updatedAt: string;
  /** Статус строки mr_lobbies: open | ready | starting */
  lobbyStatus?: string;
  /** Последняя фаза связанного матча (если есть) */
  matchPhase?: string | null;
};

export type MrLobbyBrowserResponse = {
  ok: boolean;
  lobbies: MrLobbyBrowserEntry[];
};

export type MrLobbyChatMessage = {
  id: number;
  lobbyId: number;
  userId: number;
  displayName: string;
  message: string;
  createdAt: string;
};

export type MrLobbyChatResponse = {
  ok: boolean;
  messages: MrLobbyChatMessage[];
};

export type MrVisibleMarker = {
  kind: string;
  stepId?: string | null;
  taskId?: string | null;
  targetId?: string | null;
  roomId?: string | null;
  interactableId?: string | null;
  x?: number | null;
  y?: number | null;
  label?: string | null;
  action?: string | null;
  required?: boolean | null;
};

export type MrNpcSnapshot = {
  npcId: string;
  name: string;
  role?: string | null;
  x: number;
  y: number;
  roomId?: string | null;
  state?: string | null;
  alertLevel?: number | null;
  path?: Array<[number, number]>;
  speed?: number;
  startedAtMs?: number;
  etaMs?: number;
  startedX?: number;
  startedY?: number;
  patrolNodeIndex?: number;
  visionCone?: {
    angle?: number;
    range?: number;
  };
};

export type MrDoorSnapshot = {
  id: string;
  locked: boolean;
  open: boolean;
  label?: string | null;
  requiredAccess?: string | null;
  accessMode?: string | null;
  accessCardObjectIds?: string[];
  fromZoneId?: string | null;
  toZoneId?: string | null;
};

export type MrActorCommand = {
  commandId: string;
  actorType: 'player' | 'bot' | 'npc' | string;
  actorId: string;
  command: 'move' | 'interact' | 'shoot' | 'equip' | 'toggle_flashlight' | 'inspect' | 'secure' | 'ping' | 'hide' | 'unhide' | 'accuse' | string;
  t?: number;
  targetX?: number;
  targetY?: number;
  startedX?: number;
  startedY?: number;
  path?: Array<[number, number]>;
  speed?: number;
  startedAtMs?: number;
  etaMs?: number;
  interactableId?: string | null;
  targetId?: string | null;
  roomId?: string | null;
  itemId?: string | null;
  enabled?: boolean;
  fromX?: number;
  fromY?: number;
  toX?: number;
  toY?: number;
  x?: number;
  y?: number;
  kind?: string | null;
};

export type MrTaskSummary = {
  total: number;
  completed: number;
  requiredTotal: number;
  requiredCompleted: number;
};

export type MrInventoryItem = {
  id: string;
  label: string;
  kind: string;
  isWeapon?: boolean;
  acquiredAtMs?: number;
  quantity: number;
  slotIndex: number;
  icon?: string;
  iconAssetId?: string;
  iconAssetUrl?: string;
  iconAssetName?: string;
  iconAssetMimeType?: string;
  description?: string;
  sourceObjectId?: string;
};

export type MrPersonalTask = {
  taskId: string;
  title: string;
  kind: string;
  trigger: string;
  description?: string;
  actionLabel?: string;
  markerLabel?: string;
  roomId?: string;
  interactableId?: string;
  targetIds?: string[];
  completedTargetIds?: string[];
  target: number;
  progress: number;
  completed: boolean;
  required: boolean;
  reward: number;
  completedAtMs?: number | null;
};

export type MrMatchSnapshot = {
  mapId?: string;
  mapDefinition?: unknown;
  players: Array<{
    playerId: string;
    userId?: number | null;
    displayName: string;
    isBot?: boolean;
    botStrategy?: string | null;
    botDifficulty?: string | null;
    characterId?: string | null;
    avatarUrl?: string | null;
    health?: number;
    maxHealth?: number;
    x: number;
    y: number;
    roomId: string;
    visionRadius?: number;
    movement: {
      state: 'idle' | 'moving';
      targetX: number;
      targetY: number;
      path: Array<[number, number]>;
      speed: number;
      startedAtMs: number;
      etaMs: number;
    };
    taskSummary?: MrTaskSummary;
    inventory?: MrInventoryItem[];
    activeInventoryItemId?: string | null;
    activeInventoryItemKind?: string | null;
    activeInventoryItemIsWeapon?: boolean;
    activeInventoryItemLabel?: string | null;
    flashlightEnabled?: boolean;
  }>;
  npcs: MrNpcSnapshot[];
  actorCommands?: MrActorCommand[];
  publicEvents: unknown[];
  visibleMarkers: MrVisibleMarker[];
  usedInteractableIds?: string[];
  roomStates: unknown[];
  doors?: MrDoorSnapshot[];
  suspicion: unknown[];
  evidence: unknown[];
  prevention: number;
  bidding?: {
    basePayout: number;
    currentPayout: number;
    acceptedCount: number;
    selfAccepted: boolean;
    finalized?: boolean;
  };
  contract?: {
    contractId?: string | null;
    title?: string | null;
    family?: string | null;
    progress: number;
    completed: boolean;
    stepsTotal: number;
    currentStepIndex: number;
    currentStep?: {
      stepId?: string | null;
      roomId?: string | null;
      interactableId?: string | null;
      title?: string | null;
      brief?: string | null;
      action?: string | null;
    } | null;
  };
  selfState?: {
    isHidden: boolean;
    hideZoneId?: string | null;
    taskOffers: Array<{
      taskId: string;
      kind: string;
      reward: number;
    }>;
    personalTasks?: MrPersonalTask[];
    taskSummary?: MrTaskSummary;
  };
  assignment?: {
    selfPlayerId?: string | null;
    selfRole?: string | null;
    isSelfCriminal?: boolean;
    isSelfSecurity?: boolean;
    assignedAtMs?: number | null;
  };
  accusation?: {
    accusedPlayerId?: string | null;
  };
  result?: {
    resolvedAtMs?: number;
    winnerSide?: 'defenders' | 'criminal';
    crimeCompleted?: boolean;
    criminalExposed?: boolean;
    criminalPlayerId?: string | null;
    accusedPlayerId?: string | null;
    prevention?: number;
    payout?: number;
    self?: {
      role?: string;
      moneyDelta?: number;
      ratingDelta?: number;
      winner?: boolean;
      tasksCompleted?: number;
      tasksTotal?: number;
      requiredTasksCompleted?: number;
      requiredTasksTotal?: number;
      taskReward?: number;
    };
  };
};

export type MrMatchPollResponse = {
  ok: boolean;
  serverNowMs: number;
  ackClientSeq?: number;
  match: {
    id: number;
    phase: string;
    version: number;
    phaseEndsAtMs: number;
  };
  self: {
    playerId: string;
    role: string;
    characterId: string;
    moneyPreview: number;
    ratingPreview: number;
  };
  snapshot: MrMatchSnapshot | null;
};

export type MrMatchLeaveResponse = {
  ok: boolean;
  left: boolean;
  matchId: number;
  serverVersion: number;
};

export type MrActionResponse = {
  ok: boolean;
  accepted: boolean;
  duplicate?: boolean;
  serverVersion: number;
  serverNowMs?: number;
  ackClientSeq?: number;
  phase?: string;
  phaseEndsAtMs?: number;
  correction?: {
    x: number;
    y: number;
    path: Array<[number, number]>;
  };
  snapshot?: MrMatchSnapshot;
};

export async function mrApiProfile(): Promise<MrProfileResponse> {
  return apiClient.request<MrProfileResponse>('/api/games/mystery-room/profile/');
}

export async function mrApiStats(limit = 10): Promise<MrStatsResponse> {
  return apiClient.request<MrStatsResponse>(`/api/games/mystery-room/stats/?limit=${encodeURIComponent(String(limit))}`);
}

export async function mrApiFriends(): Promise<MrFriend[]> {
  const response = await apiClient.request<unknown>('/api/chat/friends/');
  return Array.isArray(response) ? (response as MrFriend[]) : [];
}

export async function mrApiInviteFriend(userId: number, message: string): Promise<unknown> {
  const browserCrypto = typeof globalThis !== 'undefined' ? globalThis.crypto : undefined;
  const clientId =
    browserCrypto?.randomUUID?.() ??
    `mr_invite_${Date.now().toString(36)}_${Math.random().toString(16).slice(2)}`;
  return apiClient.request('/api/chat/direct-messages/', {
    method: 'POST',
    body: JSON.stringify({
      to_user_id: userId,
      message,
      client_id: clientId,
      link_preview_disabled: false,
    }),
  });
}

export async function mrApiQueueJoin(characterId?: string): Promise<MrQueuePollResponse> {
  return apiClient.request<MrQueuePollResponse>('/api/games/mystery-room/queue/join/', {
    method: 'POST',
    body: JSON.stringify({ characterId }),
  });
}

export async function mrApiQueueLeave(): Promise<MrQueuePollResponse> {
  return apiClient.request<MrQueuePollResponse>('/api/games/mystery-room/queue/leave/', {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

export async function mrApiQueuePoll(): Promise<MrQueuePollResponse> {
  return apiClient.request<MrQueuePollResponse>('/api/games/mystery-room/queue/poll/');
}

export async function mrApiLobbyCreate(input: {
  isPrivate: boolean;
  characterId?: string;
  mapId?: string;
  gameMode?: MrGameMode | string;
  botDifficulty?: MrBotDifficulty | string;
  incidentLevel?: MrIncidentLevel | string;
  maxPlayers?: number;
}): Promise<MrLobbyResponse> {
  return apiClient.request<MrLobbyResponse>('/api/games/mystery-room/lobby/create/', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export async function mrApiMapsPublished(): Promise<MrMapsResponse> {
  return apiClient.request<MrMapsResponse>('/api/games/mystery-room/maps/published/');
}

export async function mrApiMapsMine(): Promise<MrMapsResponse> {
  return apiClient.request<MrMapsResponse>('/api/games/mystery-room/maps/my/');
}

export async function mrApiMapGet(mapId: string): Promise<MrMapResponse> {
  return apiClient.request<MrMapResponse>(`/api/games/mystery-room/maps/${encodeURIComponent(mapId)}/`);
}

export async function mrApiMapCreate(input: {
  title: string;
  description?: string;
  map: MysteryRoomMapDefinition;
  schemaVersion?: number;
}): Promise<MrMapResponse> {
  return apiClient.request<MrMapResponse>('/api/games/mystery-room/maps/create/', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export async function mrApiMapUpdate(
  mapId: string,
  input: {
    title: string;
    description?: string;
    map?: MysteryRoomMapDefinition;
    schemaVersion?: number;
  },
): Promise<MrMapResponse> {
  return apiClient.request<MrMapResponse>(`/api/games/mystery-room/maps/${encodeURIComponent(mapId)}/update/`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export async function mrApiMapPublish(mapId: string): Promise<MrMapResponse> {
  return apiClient.request<MrMapResponse>(`/api/games/mystery-room/maps/${encodeURIComponent(mapId)}/publish/`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

export async function mrApiMapDuplicate(mapId: string): Promise<MrMapResponse> {
  return apiClient.request<MrMapResponse>(`/api/games/mystery-room/maps/${encodeURIComponent(mapId)}/duplicate/`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

export async function mrApiMapDelete(mapId: string): Promise<{ ok: boolean; deleted: boolean }> {
  return apiClient.request<{ ok: boolean; deleted: boolean }>(`/api/games/mystery-room/maps/${encodeURIComponent(mapId)}/delete/`, {
    method: 'DELETE',
  });
}

export async function mrApiMapUploadAsset(mapId: string, file: File): Promise<MrMapAssetResponse> {
  const formData = new FormData();
  formData.append('file', file);
  return apiClient.request<MrMapAssetResponse>(`/api/games/mystery-room/maps/${encodeURIComponent(mapId)}/assets/upload/`, {
    method: 'POST',
    body: formData,
  });
}

export async function mrApiMapDeleteAsset(mapId: string, assetId: string): Promise<{ ok: boolean; deleted: boolean }> {
  return apiClient.request<{ ok: boolean; deleted: boolean }>(
    `/api/games/mystery-room/maps/${encodeURIComponent(mapId)}/assets/${encodeURIComponent(assetId)}/delete/`,
    {
      method: 'DELETE',
    },
  );
}

export async function mrApiObjectSkins(): Promise<MrObjectSkinsResponse> {
  return apiClient.request<MrObjectSkinsResponse>('/api/games/mystery-room/object-skins/');
}

export async function mrApiObjectSkinSave(input: {
  id?: string;
  kind: string;
  itemId?: string;
  title: string;
  isDefault?: boolean;
  payload: MrObjectSkinPayload;
}): Promise<MrObjectSkinResponse> {
  const endpoint = input.id
    ? `/api/games/mystery-room/object-skins/${encodeURIComponent(input.id)}/`
    : '/api/games/mystery-room/object-skins/';
  return apiClient.request<MrObjectSkinResponse>(endpoint, {
    method: input.id ? 'PUT' : 'POST',
    body: JSON.stringify(input),
  });
}

export async function mrApiObjectSkinDelete(id: string): Promise<{ ok: boolean; deleted: boolean }> {
  return apiClient.request<{ ok: boolean; deleted: boolean }>(`/api/games/mystery-room/object-skins/${encodeURIComponent(id)}/`, {
    method: 'DELETE',
  });
}

export async function mrApiLobbyJoin(input: { lobbyCode?: string; lobbyId?: number; characterId?: string }): Promise<MrLobbyResponse> {
  return apiClient.request<MrLobbyResponse>('/api/games/mystery-room/lobby/join/', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export async function mrApiLobbyLeave(input: { lobbyId: number }): Promise<MrLobbyResponse> {
  return apiClient.request<MrLobbyResponse>('/api/games/mystery-room/lobby/leave/', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

/** Распуск лобби владельцем: всех выкидывает из MR-лобби (poll → 403). */
export async function mrApiLobbyDisband(input: { lobbyId: number }): Promise<MrLobbyResponse> {
  return apiClient.request<MrLobbyResponse>('/api/games/mystery-room/lobby/disband/', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export async function mrApiLobbyReady(input: { lobbyId: number; ready: boolean }): Promise<MrLobbyResponse> {
  return apiClient.request<MrLobbyResponse>('/api/games/mystery-room/lobby/ready/', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export async function mrApiLobbySettings(input: {
  lobbyId: number;
  mapId?: string;
  gameMode?: MrGameMode | string;
  botDifficulty?: MrBotDifficulty | string;
  incidentLevel?: MrIncidentLevel | string;
  maxPlayers?: number;
}): Promise<MrLobbyResponse> {
  return apiClient.request<MrLobbyResponse>('/api/games/mystery-room/lobby/settings/', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export async function mrApiLobbySlot(input: {
  lobbyId: number;
  slotIndex?: number;
  action: 'take' | 'open' | 'close' | 'bot' | 'kick' | 'leave';
  botDifficulty?: MrBotDifficulty | string;
  botStrategy?: string;
}): Promise<MrLobbyResponse> {
  return apiClient.request<MrLobbyResponse>('/api/games/mystery-room/lobby/slot/', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export async function mrApiLobbyStartBots(input: { lobbyId: number; targetPlayers?: number }): Promise<MrLobbyResponse> {
  return apiClient.request<MrLobbyResponse>('/api/games/mystery-room/lobby/start-bots/', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export async function mrApiLobbyDevFill(input: { lobbyId: number; targetPlayers?: number }): Promise<MrLobbyResponse> {
  return apiClient.request<MrLobbyResponse>('/api/games/mystery-room/lobby/dev-fill/', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export async function mrApiLobbyPoll(lobbyId: number): Promise<MrLobbyResponse> {
  return apiClient.request<MrLobbyResponse>(`/api/games/mystery-room/lobby/poll/?lobbyId=${lobbyId}`);
}

export async function mrApiLobbyCurrent(): Promise<MrLobbyResponse> {
  return apiClient.request<MrLobbyResponse>('/api/games/mystery-room/lobby/current/');
}

export async function mrApiLobbyChatList(lobbyId: number): Promise<MrLobbyChatResponse> {
  return apiClient.request<MrLobbyChatResponse>(`/api/games/mystery-room/lobby/chat/?lobbyId=${lobbyId}`);
}

export async function mrApiLobbyChatSend(input: { lobbyId: number; message: string }): Promise<MrLobbyChatResponse> {
  return apiClient.request<MrLobbyChatResponse>('/api/games/mystery-room/lobby/chat/', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export async function mrApiLobbyPublicList(limit = 40): Promise<MrLobbyBrowserResponse> {
  return apiClient.request<MrLobbyBrowserResponse>(`/api/games/mystery-room/lobby/public-list/?limit=${Math.max(1, Math.min(100, limit))}`);
}

export async function mrApiLobbyFriendsList(limit = 40): Promise<MrLobbyBrowserResponse> {
  return apiClient.request<MrLobbyBrowserResponse>(`/api/games/mystery-room/lobby/friends-list/?limit=${Math.max(1, Math.min(100, limit))}`);
}

export async function mrApiMatchPoll(matchId: number, sinceVersion = 0): Promise<MrMatchPollResponse> {
  return apiClient.request<MrMatchPollResponse>(
    `/api/games/mystery-room/match/poll/?matchId=${matchId}&sinceVersion=${sinceVersion}`,
  );
}

export async function mrApiMatchLeave(matchId: number): Promise<MrMatchLeaveResponse> {
  return apiClient.request<MrMatchLeaveResponse>('/api/games/mystery-room/match/leave/', {
    method: 'POST',
    body: JSON.stringify({ matchId }),
  });
}

export async function mrApiMatchAction(input: {
  matchId: number;
  clientSeq: number;
  action: 'move' | 'chat' | 'ping' | 'accuse' | 'bid_accept' | 'bid_cancel' | 'hide' | 'unhide' | 'inspect' | 'secure' | 'interact' | 'pickup' | 'equip' | 'toggle_flashlight' | 'shoot';
  payload: Record<string, unknown>;
}): Promise<MrActionResponse> {
  return apiClient.request<MrActionResponse>('/api/games/mystery-room/match/action/', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export async function mrApiMatchChat(input: {
  matchId: number;
  clientSeq: number;
  message: string;
}): Promise<MrActionResponse> {
  return apiClient.request<MrActionResponse>('/api/games/mystery-room/match/chat/', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export async function mrApiMatchPing(input: {
  matchId: number;
  clientSeq: number;
  x: number;
  y: number;
  kind: string;
}): Promise<MrActionResponse> {
  return apiClient.request<MrActionResponse>('/api/games/mystery-room/match/ping/', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export async function mrApiMatchAccuse(input: {
  matchId: number;
  clientSeq: number;
  accusedPlayerId: string;
}): Promise<MrActionResponse> {
  return apiClient.request<MrActionResponse>('/api/games/mystery-room/match/accuse/', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}
